import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTopicUIStore } from '../../store/topicUIStore';
import { ProjectCreationModal } from './ProjectCreationModal';
import { ProjectItem } from './ProjectItem';
import { ProjectSkeleton } from './ProjectSkeleton';
import { SidebarHeader } from '../sidebar/SidebarHeader';
import { SidebarFooter } from '../sidebar/SidebarFooter';
import { useUser } from '../../hooks/queries/useAuth';
import { 
  useProjects, 
  useCreateProject,
  useUpdateProject
} from '../../hooks/queries/useProjects';
import { ArrowLeft, Eye, Plus } from 'lucide-react';
import { cn } from '../../lib/utils';
import { enforceProjectCreationLimit, canAccessProject } from '../../lib/project-limits';
import { isProjectAccessCached } from '../../lib/offline-db';
import { logger } from '../../utils/logger';
import { useViewport } from '../../contexts/ViewportContext';
import { useStatusBarInfo } from '../../hooks/useStatusBarInfo';
import { useToastUIStore } from '../../store/toastUIStore';

interface ProjectListProps {
  activeProjectId: string | null;
  onProjectSelect: (id: string) => void;
  onCollapse?: () => void;
  hideHeader?: boolean;
}

export function ProjectList({
  activeProjectId,
  onProjectSelect,
  onCollapse,
  hideHeader = false,
}: ProjectListProps) {
  const navigate = useNavigate();
  const { setActiveTopicId } = useTopicUIStore();
  const { data: user, isLoading: authLoading } = useUser();
  const [showArchived, setShowArchived] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isNewProjectModalOpen, setIsNewProjectModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isHeaderShadowVisible, setIsHeaderShadowVisible] = useState(false);
  const projectListRef = useRef<HTMLDivElement>(null);
  const { addToast } = useToastUIStore();
  
  // React Query hooks
  const { data: projects = [], isLoading: projectsLoading } = useProjects(showArchived);
  const createProjectMutation = useCreateProject();
  const updateProjectMutation = useUpdateProject();

  const handleNewProject = async (
    name: string,
    description: string,
    context: string,
    avatarFile?: File
  ) => {
    if (isCreating) return;

    setIsCreating(true);
    try {
      const project = await createProjectMutation.mutateAsync({
        id: crypto.randomUUID(),
        name,
        description,
        context,
        avatarFile
      });

      if (project) {
        setIsNewProjectModalOpen(false);
        setActiveTopicId(null);
        navigate(`/project/${project.id}`);
        onProjectSelect(project.id);
        addToast({
          type: 'success',
          title: 'Project created',
          message: 'Your new project has been created successfully',
        });
      }
    } catch (error) {
      logger.error('Error creating project:', error);
      addToast({
        type: 'error',
        title: 'Project creation failed',
        message: 'Failed to create project. Please try again.',
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleUpdateProject = async (id: string, updates: Partial<any>) => {
    try {
      await updateProjectMutation.mutateAsync({
        projectId: id,
        updates
      });
      return true;
    } catch (error) {
      logger.error('Error updating project:', error);
      addToast({
        type: 'error',
        title: 'Update failed',
        message: 'Failed to update project. Please try again.',
      });
      return false;
    }
  };

  const handleNewProjectClick = async () => {
    if (!user) return;

    try {
      const limitCheck = await enforceProjectCreationLimit(user.id);
      if (!limitCheck.allowed) {
        navigate('/subscription', {
          state: {
            reason: 'project_limit',
            message:
              "You've reached the maximum number of projects for the free plan.\n Upgrade to Pro to create" +
              ' unlimited projects.',
          },
          replace: true,
        });
        return;
      }
      setIsNewProjectModalOpen(true);
    } catch (error) {
      logger.error('Error checking project limits:', error);
      // Even if the check fails, allow new users to create at least one project
      // This ensures new users don't get stuck unable to create their first project
      setIsNewProjectModalOpen(true);
    }
  };

  const handleToggleArchived = () => {
    setShowArchived(!showArchived);
  };

  // Get viewport state
  const { isMobile } = useViewport();

  // Get status bar information for mobile adjustments
  const { isNativeApp, navigationBarHeight } = useStatusBarInfo();

  // Log status bar info for debugging
  React.useEffect(() => {
    if (isNativeApp && isMobile) {
      logger.capacitor('ProjectList: Using navigation bar height', {
        navigationBarHeight,
      });
    }
  }, [isNativeApp, navigationBarHeight, isMobile]);
  
  // Real-time project subscriptions are now handled internally by useProjects hook

  // Pre-cache project access for offline support
  const [cachedProjects, setCachedProjects] = useState<Set<string>>(new Set());
  
  useEffect(() => {
    if (!user || !projects.length || projectsLoading) return;
    
    // Pre-cache access for all projects when online
    if (navigator.onLine) {
      const preCacheProjectAccess = async () => {
        // Filter out already processed projects in this session
        const projectsToCheck = projects.filter(p => !cachedProjects.has(p.id));
        
        if (projectsToCheck.length === 0) return;
        
        // Check cache status for all projects in parallel
        const cacheChecks = await Promise.allSettled(
          projectsToCheck.map(async (project) => {
            try {
              const isCached = await isProjectAccessCached(user.id, project.id);
              return { project, isCached, error: null };
            } catch (error) {
              // If IndexedDB fails, mark as error
              return { project, isCached: false, error };
            }
          })
        );
        
        const uncachedProjects: typeof projects = [];
        const newlyCachedIds: string[] = [];
        
        // Process results
        cacheChecks.forEach((result) => {
          if (result.status === 'fulfilled') {
            const { project, isCached, error } = result.value;
            if (error) {
              logger.error('Failed to check project access cache', { projectId: project.id, error });
              uncachedProjects.push(project);
            } else if (!isCached) {
              uncachedProjects.push(project);
            } else {
              newlyCachedIds.push(project.id);
            }
          }
        });
        
        // Update cached projects set in one batch
        if (newlyCachedIds.length > 0) {
          setCachedProjects(prev => new Set([...prev, ...newlyCachedIds]));
        }
        
        if (uncachedProjects.length === 0) return;
        
        logger.store(`Pre-caching access for ${uncachedProjects.length} uncached projects`);
        
        // Check access for uncached projects in the background
        const cachePromises = uncachedProjects.map(async (project) => {
          try {
            await canAccessProject(user.id, project.id);
            // Mark as cached after successful caching
            setCachedProjects(prev => new Set([...prev, project.id]));
          } catch (error) {
            logger.error('Failed to pre-cache project access', { projectId: project.id, error });
          }
        });
        
        await Promise.allSettled(cachePromises);
      };
      
      preCacheProjectAccess();
    }
  }, [user, projects, projectsLoading]); // Note: cachedProjects intentionally excluded to prevent infinite loop

  // Add scroll detection for header shadow only - mobile only
  useEffect(() => {
    // Only apply shadows on mobile devices
    if (!isMobile) {
      setIsHeaderShadowVisible(false);
      return;
    }

    const handleScroll = () => {
      if (projectListRef.current) {
        const container = projectListRef.current;
        const scrollTop = container.scrollTop;

        // Show header shadow when scrolled down
        setIsHeaderShadowVisible(scrollTop > 10);
      }
    };

    const scrollContainer = projectListRef.current;
    if (scrollContainer) {
      // Initial check
      setTimeout(handleScroll, 100);

      scrollContainer.addEventListener('scroll', handleScroll);
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
  }, [isMobile]);

  // Only show loading state on initial load, not during transitions
  const showLoading = projectsLoading && projects.length === 0;

  const renderProjects = () => {
    if (showLoading) {
      return (
        <div className="pt-2">
          <ProjectSkeleton />
        </div>
      );
    }

    // Ensure projects is always an array even if undefined
    const projectsList = projects || [];

    // Separate owned and shared projects
    const ownedProjects = projectsList.filter(p => p.isOwner);
    const sharedProjects = projectsList.filter(p => !p.isOwner);

    return (
      <>
        {showArchived ? (
          projectsList.length > 0 ? (
            <>
              <div className="px-4 py-2 text-sm font-medium text-gray-400">Archived Projects</div>
              {projectsList.map(project => (
                <ProjectItem
                  key={project.id}
                  project={project}
                  projectRole={project.isOwner ? 'owner' : 'guest'}
                  isActive={project.id === activeProjectId}
                  onClick={() => onProjectSelect(project.id)}
                  onUpdate={handleUpdateProject}
                />
              ))}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center px-6 py-8">
              <h3 className="text-gray-400 font-medium mb-2">No Archived Projects</h3>
              <p className="text-gray-500 text-sm">See you later!</p>
            </div>
          )
        ) : (
          <>
            {/* My Projects Section */}
            {projectsList.length > 0 ? (
              <>
                {/* Show owned projects if any */}
                {ownedProjects.length > 0 && (
                  <>
                    {ownedProjects.map(project => (
                      <ProjectItem
                        key={project.id}
                        project={project}
                        projectRole="owner"
                        isActive={project.id === activeProjectId}
                        onClick={() => onProjectSelect(project.id)}
                        onUpdate={handleUpdateProject}
                      />
                    ))}
                  </>
                )}

                {/* New Project Button - Always visible when any projects exist (owned or shared) */}
                <button
                  onClick={handleNewProjectClick}
                  className={cn(
                    'relative w-full p-3 transition-colors cursor-pointer group text-left'
                  )}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-500/20 transition-colors shadow">
                      <Plus size={20} className="text-gray-700" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3
                        className={cn(
                          'text-base font-medium text-blue-600 truncate',
                          isMobile && 'text-lg'
                        )}
                      >
                        New Project
                      </h3>
                      <p className={cn('text-xs text-gray-400 truncate', isMobile && 'text-sm')}>
                        Let's get this done!
                      </p>
                    </div>
                  </div>
                </button>
              </>
            ) : (
              // Empty state when no projects exist at all - with clickable icon
              <div className="flex flex-col items-center justify-center px-4 py-12 text-center">
                <button
                  onClick={handleNewProjectClick}
                  className="w-20 h-20 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center mb-4 transition-colors cursor-pointer shadow-lg"
                >
                  <Plus size={40} className="text-blue-600" />
                </button>
                <h3 className="text-lg font-medium text-gray-800 mb-2">No projects yet</h3>
                <p className="text-gray-600 text-sm mb-1">
                  Create your first project to get started
                </p>
              </div>
            )}

            {/* Shared Projects Section */}
            {sharedProjects.length > 0 && (
              <>
                <div className="px-4 py-4 text-sm font-medium text-gray-400 uppercase mt-4">
                  Shared With Me
                </div>
                {sharedProjects.map(project => (
                  <ProjectItem
                    key={project.id}
                    project={project}
                    projectRole="guest"
                    isActive={project.id === activeProjectId}
                    onClick={() => onProjectSelect(project.id)}
                    onUpdate={handleUpdateProject}
                  />
                ))}
              </>
            )}
          </>
        )}
      </>
    );
  };

  return (
    <div className="flex flex-col h-full ProjectList">
      {!hideHeader && (
        <SidebarHeader
          onCollapse={!showArchived ? onCollapse : undefined}
          showShadow={isHeaderShadowVisible}
        >
          {showArchived && (
            <button
              onClick={handleToggleArchived}
              className="flex items-center gap-2 text-gray-400 hover:text-gray-700 transition-colors"
            >
              <ArrowLeft size={16} />
              <span>Back to Projects</span>
            </button>
          )}
        </SidebarHeader>
      )}

      {/* Overview Button above project list */}
      <div className="px-4 pb-3">
        <button
          onClick={() => navigate('/dashboard')}
          className="w-full py-1.5 px-3 rounded-md bg-blue-500 hover:bg-blue-600 text-white shadow-sm hover:shadow transition-all"
        >
          <div className="flex items-center justify-center gap-2.5">
            <Eye size={18} className="text-white" />
            <span className="font-medium">Overview</span>
          </div>
        </button>
      </div>

      {/* Project List */}
      <div
        ref={projectListRef}
        className="flex-1 overflow-y-auto"
        style={{
          paddingBottom: isNativeApp && isMobile ? `${navigationBarHeight}px` : '0',
        }}
      >
        {renderProjects()}
      </div>

      {/* User Profile Footer - only show on desktop */}
      {!isMobile && (
        <div className="flex-shrink-0 mt-auto">
          <SidebarFooter
            user={user}
            loading={authLoading}
            isMenuOpen={isMenuOpen}
            onMenuToggle={setIsMenuOpen}
          />
        </div>
      )}

      <ProjectCreationModal
        isOpen={isNewProjectModalOpen}
        onClose={() => setIsNewProjectModalOpen(false)}
        onSubmit={handleNewProject}
        isSubmitting={isCreating}
      />
    </div>
  );
}